import { ColumnDef } from '@tanstack/react-table';
import dotize from 'dotize';
import { t } from 'i18next';
import { CircleAlertIcon, CircleCheckIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Badge } from '@/components/ui/badge';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { COLUMN_PRESETS } from '@/util/constants';

import { getDebtCampaignVariables } from '../../../constants';
import { isValidDebtAmountCell, isValidDebtCurrencyCell } from '../../../utils';
import { getErrorColumns } from '../../../utils/subscriberErrors';

export type Subscriber = Record<string, unknown>;

/**
 * Rewrite a slotted debt key into the bracket form used as the column header.
 * Redux subscribers store flat keys like `debt.0.amount`; API rows surface as
 * `debts[0].amount`. Display both as `debts[0].amount` so the column heading
 * matches the CSV header shape users uploaded.
 */
const formatSlotKey = (key: string): string => key.replace(/^debt\.(\d+)\.(.+)$/, 'debts[$1].$2');

/**
 * Extract alternate phones / emails from either Redux (flat slot-indexed keys)
 * or API (`context.alternate_phones: [...]`).
 */
const extractAlternates = (subscriber: Subscriber, kind: 'phones' | 'emails'): string[] => {
  const bucket = kind === 'phones' ? 'alternate_phones' : 'alternate_emails';
  const ctx = subscriber.context as Record<string, unknown> | undefined;
  if (ctx && Array.isArray(ctx[bucket])) {
    return (ctx[bucket] as unknown[]).filter(
      (v): v is string => typeof v === 'string' && v.length > 0
    );
  }
  const out: { idx: number; value: string }[] = [];
  for (const [key, value] of Object.entries(subscriber)) {
    const match = key.match(new RegExp(`^${bucket}\\.(\\d+)$`));
    if (!match || !value || typeof value !== 'string') {
      continue;
    }
    out.push({ idx: parseInt(match[1], 10), value });
  }
  return out.sort((a, b) => a.idx - b.idx).map((x) => x.value);
};

/**
 * Keys that the composite Alternates columns absorb — alternates are always
 * collapsed into a single column since they're list-shaped by nature.
 * Debt slot keys are NOT absorbed: each `debt.<N>.<field>` (or its API
 * equivalent `context.debts[<N>].<field>`) renders as its own column.
 */
const ALTERNATES_ABSORBED_REGEXES = [
  /^alternate_phones\.\d+$/,
  /^alternate_emails\.\d+$/,
  /^context\.alternate_phones(\..+)?$/,
  /^context\.alternate_emails(\..+)?$/,
];

const isAbsorbedKey = (key: string): boolean =>
  ALTERNATES_ABSORBED_REGEXES.some((re) => re.test(key));

/**
 * Gets a value from an object using dot notation
 * Uses dotize library for consistent behavior across the app
 *
 * @param obj - The object to access
 * @param path - Dot notation path (e.g., 'address.street')
 * @returns The value at the specified path
 */
export function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const flattened = dotize.convert(obj);
  return flattened[path];
}

/**
 * Formats column header text for display
 * Dotted / bracketed keys (e.g. `user.display_name`, `debts[0].amount`) are
 * passed through verbatim so the header matches what the user typed in the
 * CSV. Plain keys are Title-Cased for readability.
 *
 * @param key - The column key
 * @param useRawDottedNotation - If true, dotted/bracketed keys are kept as-is
 * @returns Formatted header text
 */
export function formatColumnHeader(key: string, useRawDottedNotation = true): string {
  // Preserve the user's casing for path-shaped keys.
  if (useRawDottedNotation && /[.[]/.test(key)) {
    return key;
  }

  // Handle all naming conventions by replacing separators with spaces
  return key
    .replace(/\./g, ' ') // Replace dots with spaces
    .replace(/[-_]/g, ' ') // Replace kebab-case and snake_case
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters (camelCase)
    .split(' ')
    .filter((word) => word.length > 0) // Remove empty strings
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
}

type FlatRow = Record<string, unknown>;

// One flatten per row — dotize walks the whole object, so probes must share it.
const flattenRow = (sub: Subscriber): FlatRow => dotize.convert(sub) as FlatRow;

const NAME_KEYS = [
  'user.display_name',
  'context.user.display_name',
  'name',
  'email',
  'context.email',
  'phone',
  'context.phone',
];

const subscriberNameFromFlat = (flat: FlatRow): string => {
  for (const key of NAME_KEYS) {
    const value = flat[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

/** Best-effort display name for a subscriber, falling back to email/phone. */
export const getSubscriberName = (sub: Subscriber): string =>
  subscriberNameFromFlat(flattenRow(sub));

/** Human label per errored column key, matching the audience variable labels. */
const ERROR_COLUMN_LABELS = new Map(getDebtCampaignVariables().map((v) => [v.name, v.label]));

export interface ErrorChip {
  /** Errored column key — stable id used for per-chip dismissal. */
  key: string;
  /** Human-readable column label. */
  column: string;
  /** The offending cell value (`empty` if blank), or the subscriber for a Contact chip. */
  value: string;
}

/** Sentinel key for the "no contact / identity" chip (not a real column). */
const CONTACT_CHIP_KEY = '__contact__';

/** Read a subscriber's value for an errored column key (folds a debt slot). */
const errorCellValue = (flat: FlatRow, col: string): string => {
  const slotKey = col.replace(/^debt\./, 'debt.0.');
  const raw = flat[col] ?? (slotKey === col ? undefined : flat[slotKey]);
  return typeof raw === 'string' || typeof raw === 'number' ? String(raw).trim() : '';
};

// Required debt-spec columns, derived so the variable definitions stay the source of truth.
const DEBT_SPEC_COLUMNS = getDebtCampaignVariables()
  .filter((v) => v.required && v.name.startsWith('debt.'))
  .map((v) => v.name);

const isDebtCellInvalid = (flat: FlatRow, col: string): boolean => {
  const value = errorCellValue(flat, col);
  if (!value) {
    return true;
  }
  if (col === 'debt.amount') {
    return !isValidDebtAmountCell(value);
  }
  if (col === 'debt.currency') {
    return !isValidDebtCurrencyCell(value);
  }
  return false;
};

// A bare `debts` field → the offending debt cell(s), defaulting to Amount.
const debtChipKeys = (flat: FlatRow): string[] => {
  const bad = DEBT_SPEC_COLUMNS.filter((col) => isDebtCellInvalid(flat, col));
  return bad.length > 0 ? bad : ['debt.amount'];
};

const chipKeysFromFlat = (sub: Subscriber, flat: FlatRow): string[] => {
  const field = (sub as { errorField?: unknown }).errorField;
  const fieldStr = typeof field === 'string' ? field : undefined;
  const fields = fieldStr
    ? fieldStr
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean)
    : [];

  // A `debts`/`debt` requirement refines to the actually-invalid debt cell(s) per
  // row; co-reported fields (e.g. country_code) keep their own columns. Splitting
  // first stops a combined `debts,country_code` from chipping a valid Currency cell.
  if (fields.some((f) => f === 'debts' || f === 'debt')) {
    const keySet = new Set<string>();
    for (const f of fields) {
      const cols = f === 'debts' || f === 'debt' ? debtChipKeys(flat) : getErrorColumns(f);
      cols.forEach((col) => keySet.add(col));
    }
    return [...keySet];
  }

  const columns = getErrorColumns(fieldStr);
  if (columns.length > 0) {
    return columns;
  }
  const emailEmpty = !errorCellValue(flat, 'email');
  const phoneEmpty = !errorCellValue(flat, 'phone');
  if (!emailEmpty && !phoneEmpty) {
    return [CONTACT_CHIP_KEY];
  }
  const keys: string[] = [];
  if (emailEmpty) {
    keys.push('email');
  }
  if (phoneEmpty) {
    keys.push('phone');
  }
  return keys;
};

/** Chip keys an errored subscriber maps to — keeps the chip filter and buildErrorChips in sync. */
export const getSubscriberErrorChipKeys = (sub: Subscriber): string[] =>
  chipKeysFromFlat(sub, flattenRow(sub));

/**
 * One chip per errored column (`Column is <value>`, `+N` for extra rows); `value`
 * is the first offending cell (`empty` if blank), never the name. Columns come from
 * `getErrorColumns`; a no-column error flags the blank email/phone, else a `Contact`
 * chip. `Contact` and the blank-cell value are translated; column labels are not.
 */
export const buildErrorChips = (errorSubscribers: Subscriber[]): ErrorChip[] => {
  const byColumn = new Map<string, { column: string; first: string; count: number }>();
  const emptyValue = t('campaigns.audience_warnings.empty_value');

  const columnLabel = (col: string): string =>
    col === CONTACT_CHIP_KEY
      ? t('campaigns.audience_warnings.contact')
      : (ERROR_COLUMN_LABELS.get(col) ?? formatColumnHeader(col));

  for (const sub of errorSubscribers) {
    const flat = flattenRow(sub);
    for (const col of chipKeysFromFlat(sub, flat)) {
      const group = byColumn.get(col);
      if (group) {
        group.count += 1;
        continue;
      }
      // Only the first row per column pays for the display value.
      const value =
        col === CONTACT_CHIP_KEY
          ? subscriberNameFromFlat(flat) || emptyValue
          : errorCellValue(flat, col) || emptyValue;
      byColumn.set(col, { column: columnLabel(col), first: value, count: 1 });
    }
  }

  return [...byColumn.entries()].map(([col, group]) => ({
    key: col,
    column: group.column,
    value: group.count > 1 ? `${group.first} +${group.count - 1}` : group.first,
  }));
};

/** Segmented active-filter chip: [column | operator | value | ×]. */
export const ActiveFilterChip = ({
  column,
  operator,
  value,
  onRemove,
}: {
  column: string;
  operator: string;
  value: string;
  onRemove: () => void;
}) => {
  const { t: translate } = useTranslation();
  return (
    <div className="inline-flex items-center overflow-hidden rounded-md border border-stroke bg-bg-muted-50 text-sm leading-5">
      <span className="px-1.5 py-1 text-foreground">{column}</span>
      <span className="flex min-w-7 items-center justify-center self-stretch border-l border-stroke px-1.5 py-1 font-medium text-foreground-muted">
        {operator}
      </span>
      <span className="self-stretch border-l border-stroke px-1.5 py-1 font-medium text-foreground">
        {value}
      </span>
      <button
        type="button"
        aria-label={translate('common.remove')}
        onClick={onRemove}
        className="flex items-center self-stretch border-l border-stroke px-1.5 py-1 text-foreground-muted hover:bg-highlight-background"
      >
        <XIcon className="size-4" />
      </button>
    </div>
  );
};

/**
 * Formats a cell value for display
 *
 * @param value - The raw value
 * @param key - The column key for context
 * @returns Formatted value
 */
export function formatCellValue(value: unknown, key: string): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  // Format percentage values
  if (key.toLowerCase().includes('rate') && typeof value === 'number') {
    return `${value.toFixed(2)}%`;
  }

  // Format dates (simple detection)
  if (key.toLowerCase().includes('date') && typeof value === 'string' && value.includes('/')) {
    return value;
  }

  return String(value);
}

/**
 * Sorts column keys with priority for common fields
 * Handles both flat keys and dotted notation keys
 *
 * @param a - First column key
 * @param b - Second column key
 * @returns Sort comparison result
 */
export function sortColumnKeys(a: string, b: string): number {
  // Prioritize name fields first, then other common fields
  const priority: Record<string, number> = {
    name: 1,
    display_name: 2,
    'user.display_name': 3,
    'context.user.display_name': 3,
    phone: 4,
    email: 5,
  };

  const aPriority = priority[a] || 999;
  const bPriority = priority[b] || 999;

  if (aPriority !== bPriority) {
    return aPriority - bPriority;
  }

  // For nested properties, sort by depth first (less nested first)
  const aDepth = a.split('.').length;
  const bDepth = b.split('.').length;

  if (aDepth !== bDepth) {
    return aDepth - bDepth;
  }

  // Numeric collation so `debt.2.amount` sorts before `debt.10.amount`.
  return a.localeCompare(b, undefined, { numeric: true });
}

/**
 * Dynamically generates column definitions based on subscriber data source
 * For Redux data (newly uploaded subscribers): shows all fields from the uploaded spreadsheet (flat structure)
 * For API data (saved subscribers): shows name (from context.user.display_name), email, phone from root level,
 * and all other columns come from the context object
 *
 * @param subscribers - Array of subscriber objects
 * @param isReduxData - Whether showing Redux data (newly uploaded) or API data (saved with context)
 * @returns Array of column definitions for the table
 *
 * @example
 * const cols = createDynamicColumns(subscribersData, true);
 * <DataTable.Root columns={cols} data={subscribersData} />
 */
export function createDynamicColumns(
  subscribers: Subscriber[],
  isReduxData = false,
  { ensureRequiredColumns = false }: { ensureRequiredColumns?: boolean } = {}
): ColumnDef<Subscriber>[] {
  if (!subscribers || subscribers.length === 0) {
    return [];
  }

  const allKeys = new Set<string>();

  // Debt slot fields render as individual columns (`Debts[0].amount`,
  // `Debts[0].currency`, `Debts[1].amount`, …). Alternates still collapse
  // into a composite column since they're list-shaped by nature.
  const hasAnyAltPhones = subscribers.some((s) => extractAlternates(s, 'phones').length > 0);
  const hasAnyAltEmails = subscribers.some((s) => extractAlternates(s, 'emails').length > 0);

  if (isReduxData) {
    // For Redux data (newly uploaded), collect ALL keys from the root level (flat CSV structure)
    subscribers.forEach((subscriber) => {
      // Flatten the subscriber object to handle nested properties
      const flattened = dotize.convert(subscriber);
      Object.keys(flattened).forEach((key) => {
        // Exclude internal fields and anything absorbed by composite columns.
        if (
          key === 'error' ||
          key === 'errorField' ||
          key === 'ingestion_status' ||
          key.startsWith('context.') ||
          isAbsorbedKey(key)
        ) {
          return;
        }
        allKeys.add(key);
      });
    });

    // Ensure required fields are always shown as columns (even when no
    // subscriber row carries a value for them yet). Skip slotted debt
    // variables — those render per-slot (`debt.0.amount`, `debt.1.amount`),
    // and surfacing the unslotted canonical `debt.amount` here would just
    // add an empty placeholder column.
    if (ensureRequiredColumns) {
      const requiredVars = getDebtCampaignVariables().filter(
        (v) => (v.required || v.conditionalGroup) && !v.slotted
      );
      requiredVars.forEach((v) => allKeys.add(v.name));
    }
  } else {
    // For API data (saved subscribers), collect keys from context objects
    subscribers.forEach((subscriber) => {
      const context = subscriber.context as Record<string, unknown> | undefined;
      if (context && typeof context === 'object') {
        const flattened = dotize.convert(context);
        Object.keys(flattened).forEach((key) => {
          if (key === 'error') {
            return;
          }
          // Skip keys absorbed by the composite Alternates columns.
          if (isAbsorbedKey(`context.${key}`)) {
            return;
          }
          // For user.display_name, don't prefix with 'context.' to avoid duplication
          // since it might exist at root level in draft data
          if (key === 'user.display_name') {
            allKeys.add(key);
          } else {
            // Prefix with 'context.' so we can access it correctly from row.original
            allKeys.add(`context.${key}`);
          }
        });
      }
    });

    // Add email, phone, and user.display_name from root level for saved campaigns
    allKeys.add('email');
    allKeys.add('phone');
    allKeys.add('user.display_name');
  }

  // Convert to array and sort for consistent ordering
  const sortedKeys = Array.from(allKeys).sort(sortColumnKeys);

  const dataColumns = sortedKeys.map((key, index) => {
    const isFlexColumn = index === 0;
    const preset = isFlexColumn ? ('FLEX' as const) : ('LG' as const);
    const columnConfig = { ...COLUMN_PRESETS[preset] };

    // Display key: strip `context.` prefix and rewrite `debt.<N>.<field>`
    // (Redux) to the same `debts[<N>].<field>` shape we use for API rows.
    const displayKey = formatSlotKey(
      key.startsWith('context.') ? key.replace('context.', '') : key
    );
    // `user.display_name` is an API field, not a CSV header: show "Name".
    const label =
      displayKey === 'user.display_name' ? t('common.name') : formatColumnHeader(displayKey);

    return {
      // Namespaced id so a CSV header can't collide with reserved ids (status, alternates).
      id: `data:${key}`,
      accessorKey: key,
      header: ({ column }) => <SortableHeader column={column}>{label}</SortableHeader>,
      cell: ({ row }) => {
        let value: unknown;

        if (isReduxData) {
          value = getNestedValue(row.original, key);
        } else if (key === 'user.display_name') {
          // API rows may carry display_name at root or under context — check both.
          const rootValue = getNestedValue(row.original, 'user.display_name');
          const contextValue = getNestedValue(row.original, 'context.user.display_name');
          value = rootValue || contextValue;
        } else {
          value = getNestedValue(row.original, key);
        }

        const formattedValue = formatCellValue(value, key);

        return <span className="block truncate text-sm">{formattedValue}</span>;
      },
      ...columnConfig,
      meta: { preset, label },
    };
  });

  const statusColumn: ColumnDef<Subscriber> = {
    id: 'status',
    header: '',
    enableSorting: false,
    enableHiding: false,
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM', className: 'pl-4' },
    cell: ({ row }) => {
      const errorMessage = row.original.error as string | undefined;

      if (!errorMessage) {
        return <CircleCheckIcon className="size-4 text-successful" />;
      }

      return (
        <InfoTooltip content={errorMessage} side="top" size="large">
          <CircleAlertIcon className="size-4 text-destructive" />
        </InfoTooltip>
      );
    },
  };

  const compositeColumns: ColumnDef<Subscriber>[] = [];

  if (hasAnyAltPhones) {
    compositeColumns.push({
      id: 'alternate_phones',
      header: ({ column }) => (
        <SortableHeader column={column}>{t('campaigns.audience_alternate_phones')}</SortableHeader>
      ),
      enableSorting: false,
      ...COLUMN_PRESETS.LG,
      meta: { preset: 'LG' as const, label: t('campaigns.audience_alternate_phones') },
      cell: ({ row }) => {
        const items = extractAlternates(row.original, 'phones');
        if (items.length === 0) {
          return <span className="text-sm">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {items.map((item) => (
              <Badge key={item} type="secondary" flavour="neutral" size="xs">
                {item}
              </Badge>
            ))}
          </div>
        );
      },
    });
  }

  if (hasAnyAltEmails) {
    compositeColumns.push({
      id: 'alternate_emails',
      header: ({ column }) => (
        <SortableHeader column={column}>{t('campaigns.audience_alternate_emails')}</SortableHeader>
      ),
      enableSorting: false,
      ...COLUMN_PRESETS.LG,
      meta: { preset: 'LG' as const, label: t('campaigns.audience_alternate_emails') },
      cell: ({ row }) => {
        const items = extractAlternates(row.original, 'emails');
        if (items.length === 0) {
          return <span className="text-sm">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {items.map((item) => (
              <Badge key={item} type="secondary" flavour="neutral" size="xs">
                {item}
              </Badge>
            ))}
          </div>
        );
      },
    });
  }

  return [statusColumn, ...dataColumns, ...compositeColumns];
}

/**
 * Legacy function for backwards compatibility
 * @deprecated Use createDynamicColumns instead
 */
export const columns = (): ColumnDef<Subscriber>[] => {
  return [];
};
